import { useCallback } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import AccessControl from '@gef-ui/cdu-user/components/AccessControl';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import MODALS from 'constants/modals';
import { KMP_CRATE_CLOSE_PRODUCTION_SHEET } from 'features/common/acl/constants';
import fetch from 'features/common/fetch';
import producedProduction from 'features/views/production/common';
import documents from 'features/views/production/documents';

const DocumentActionButtons = ({ role }) => {
	const dispatch = useDispatch();
	const formData = useFormData();

	const documentIdsToDelete = useSelector(documents.selectors.getDocumentIdsToDelete);
	const enableActions = useSelector(producedProduction.selectors.getEnableActions);

	const handleDeleteDocuments = useCallback(() => {
		NiceModal.show(MODALS.lightbox_modal, {
			title: getLabelMessage('producedProduction:lightBoxTitle'),
			onConfirm: () => {
				if (!isEmpty(documentIdsToDelete)) {
					dispatch(documents.actions.deleteDocuments(documentIdsToDelete, role));
				}
			},
		});
	}, [dispatch, documentIdsToDelete, role]);

	const handleOpenReportedProductionModal = useCallback(() => {
		if (formData?.withoutPpz) {
			NiceModal.show(MODALS.lightbox_modal, {
				title: getLabelMessage('producedProduction:lightBoxCloseReportWithoutPpz'),
				closeButtonLabel: getLabelMessage('common:ok'),
				showConfirmButton: false,
				onExit: () => NiceModal.show(MODALS.producedProduction_report),
			});
		} else {
			NiceModal.show(MODALS.producedProduction_report);
		}
	}, [formData?.withoutPpz]);

	const handleOpenNewProductionModal = useCallback(() => {
		NiceModal.show(MODALS.producedProduction_new);
	}, []);

	return (
		<Grid container>
			<Grid sx={{ mt: 1 }} size={12}>
				<Grid container spacing={2}>
					<Grid size={4}>
						<ComponentLoader fetchStorePath={fetch.constants.urls.productionDocumentsDelete}>
							<Button
								variant="outlined"
								onClick={handleDeleteDocuments}
								disabled={isEmpty(documentIdsToDelete)}
								fullWidth
							>
								{getLabelMessage('producedProduction:clear')}
							</Button>
						</ComponentLoader>
					</Grid>
					<AccessControl accessLevel={KMP_CRATE_CLOSE_PRODUCTION_SHEET}>
						<Grid size={4}>
							<ComponentLoader fetchStorePath={fetch.constants.urls.hasInvalidProduction}>
								<Button
									variant="contained"
									onClick={handleOpenReportedProductionModal}
									disabled={!enableActions}
									fullWidth
								>
									{getLabelMessage('producedProduction:closeReport')}
								</Button>
							</ComponentLoader>
						</Grid>
						<Grid size={4}>
							<Button
								variant="contained"
								onClick={handleOpenNewProductionModal}
								disabled={!enableActions}
								fullWidth
							>
								{getLabelMessage('producedProduction:newDocumentReport')}
							</Button>
						</Grid>
					</AccessControl>
				</Grid>
			</Grid>
		</Grid>
	);
};

DocumentActionButtons.propTypes = {
	role: PropTypes.string,
};

export default DocumentActionButtons;
